import type TableView from "../store/schema/TableView";
import type MailFolderTableQuery from "../store/schema/MailFolderTableQuery";

import { type TableQueryType } from "../store/schema/TableQuery";
import getUserCategoryName from "../selectors/getUserCategoryName";
import isInVirtualSelectionExclusionMode from "./isInVirtualSelectionExclusionMode";
import isMailListConversationView from "./isMailListConversationView";

import type FocusedViewFilter from "owa-service/lib/contract/FocusedViewFilter";
import { everythingSelectedFocused } from "owa-locstrings/lib/strings/everythingselectedfocused.locstring.json";
import { everythingSelectedSearch } from "owa-locstrings/lib/strings/everythingselectedsearch.locstring.json";
import loc, { format } from "owa-localize";
import getViewFilterForTable from "./getViewFilterForTable";
import folderStore, { getEffectiveFolderDisplayName } from "owa-folders";
import { everythingSelected } from "owa-locstrings/lib/strings/everythingselected.locstring.json";
import { everythingSelectedOther } from "owa-locstrings/lib/strings/everythingselectedother.locstring.json";
import { someItemsSelected } from "owa-locstrings/lib/strings/someitemsselected.locstring.json";
import { someConversationsSelected } from "../selectors/getItemsOrConversationsSelectedText.locstring.json";

// Returns the text for conversations or items strings in select all mode or virtual select all mode with an exclusion list.
// This is being used by the reading pane tab and reading pane. This also contains the logic of returning appropriate text
// in case of inbox plus scenario.
export default function getRowSelectionStringForSelectAll(isEverythingSelectedInTable: boolean, tableView: TableView, focusedFilter: FocusedViewFilter): string {
    const { tableQuery, virtualSelectAllExclusionList, totalRowsInView } = tableView;
    
    if (isEverythingSelectedInTable) {
        if (focusedFilter !== -1) {
            const viewFilter = getViewFilterForTable(tableView);
            if (viewFilter !== null && viewFilter !== "All") {
                return isMailListConversationView(tableView)
                    ? format(loc(someConversationsSelected), totalRowsInView)
                    : format(loc(someItemsSelected), totalRowsInView);
            }
            return focusedFilter === 0
                ? loc(everythingSelectedFocused)
                : loc(everythingSelectedOther);
            // User is in search results
        }
        if (tableQuery.type == 1) {
            return loc(everythingSelectedSearch);
            // User is in favorited category
        }
        if (getUserCategoryName(tableView)) {
            return format(loc(everythingSelected), getUserCategoryName(tableView));
        }
        
        const viewFilter = getViewFilterForTable(tableView);
        
        if (viewFilter !== null && viewFilter !== "All") {
            return isMailListConversationView(tableView)
                ? format(loc(someConversationsSelected), totalRowsInView)
                : format(loc(someItemsSelected), totalRowsInView);
        }
        
        // Everything in folder or favorited persona is selected
        const folder = folderStore().folderTable.get(tableQuery.folderId);
        let folderName = getEffectiveFolderDisplayName(folder);
        
        if ((tableView.tableQuery?.type === 0 ||
            tableView.tableQuery?.type === 1) &&
            (tableView.tableQuery as MailFolderTableQuery)?.scenarioType === "persona" &&
            folderName.indexOf("_") > 0) {
            folderName = folderName.substring(0, folderName.indexOf("_"));
        }
        
        return format(loc(everythingSelected), folderName);
    }
    // Only some rows are selected:
    // User selected all but then deselected some rows, return total item count - number of deselected items
    if (isInVirtualSelectionExclusionMode(tableView)) {
        const selectedRowsInVirtualSelectAllMode = 
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2532 (58,31): Object is possibly 'undefined'.
        // @ts-expect-error
        totalRowsInView - virtualSelectAllExclusionList.length;
        
        return isMailListConversationView(tableView)
            ? format(loc(someConversationsSelected), selectedRowsInVirtualSelectAllMode)
            : format(loc(someItemsSelected), selectedRowsInVirtualSelectAllMode);
    }
    //Ideally this would not come here, as we are calling this code either for select all mode or virtual select all with an exclusion list.
    // For code analysis, making this function return all items selected text.
    return loc(everythingSelectedSearch);
}
